import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useState } from 'react';
import { Check, Clock, AlertTriangle, Minus, ChevronDown, ChevronLeft, User, RotateCcw, Building2 } from 'lucide-react';
import {
  ORDER_STAGES_V1, CANCELLATION_STAGES_V1, resolveJourney, dispatchBlockers, journeyProgress,
  type JourneyContext, type ResolvedStage, type StageOwner, type StageLane, type StageAge,
} from '@platform/shared-types';

/**
 * THE ORDER, AS STAGES.
 *
 * The stage list is DATA (`ORDER_STAGES_V1`), not markup — this component
 * renders whatever it is given, so adding "insurance certificate" or a second
 * shipping check later changes a list, not this file.
 *
 * WHO IS THE BLOCKER is the question this screen exists to answer. Every stage
 * carries its owner, so "waiting on the partner" and "waiting on us" are never
 * the same colour.
 *
 * THE PATH IS DRAWN BY THE TILES THEMSELVES. Each stage renders the segment of
 * line beside it — its own full height, and through its own bottom margin —
 * rather than the line being one absolutely-positioned bar per run. Bars with
 * fixed heights cannot know how tall a stage's panel is, so they ended
 * wherever the guess ran out and the path dead-ended in mid-air. A tile always
 * knows its own height, so the path is continuous whatever a stage contains,
 * and its colour comes from the tile's own state — line and tick cannot
 * disagree.
 */

const OWNER_LABEL: Record<StageOwner, string> = {
  system: 'Automatic',
  rubytill: 'Us',
  customer: 'The merchant',
  iso: 'Payments partner',
  warehouse: 'Warehouse',
};

const OWNER_CHIP: Record<StageOwner, string> = {
  system: 'bg-gray-200 text-gray-700',
  rubytill: 'bg-rose-100 text-rose-700',
  customer: 'bg-amber-100 text-amber-700',
  iso: 'bg-slate-200 text-slate-700',
  warehouse: 'bg-blue-100 text-blue-700',
};

const DOT: Record<string, string> = {
  done: 'bg-emerald-500 text-white',
  active: 'bg-emerald-500 text-white ring-4 ring-emerald-500/20',
  blocked: 'bg-red-500 text-white ring-4 ring-red-500/15',
  todo: 'bg-stone-300 text-white',
  skipped: 'bg-stone-200 text-stone-400',
};

const OK = '#10b981';
const WARN = '#f59e0b';
const STUCK = '#ef4444';
const LINE = '#e7e5e4';

const AGE_COLOUR: Record<StageAge, string> = { ok: OK, warn: WARN, stuck: STUCK };
const AGE_CHIP: Record<StageAge, string> = {
  ok: 'bg-emerald-100 text-emerald-800',
  warn: 'bg-amber-100 text-amber-800',
  stuck: 'bg-red-100 text-red-700',
};

/**
 * The vein and the breath.
 *
 * A stage being worked flows — a light travels down its segment. A stage that
 * has aged STOPS flowing and breathes instead, because "moving" and "waiting"
 * must not look like the same thing at a glance. `@keyframes` cannot be
 * expressed in Tailwind utilities, so they are declared once here.
 */
const JourneyStyle: React.FC = () => (
  <style>{
    '@keyframes journeyVein { from { background-position: 0 0 } to { background-position: 0 -200% } }'
    + '@keyframes journeyBreathe { 0%,100% { opacity: 1 } 50% { opacity: .42 } }'
    /*
      THE OVERVIEW ROW. Four summary boxes, never four columns of stages.

      Fitting four lanes of full detail side by side was the thing that made
      this rail unreadable: at ~257px a lane clips its own title, and even
      with room, four columns of stages give you nothing to answer "where is
      this order" without reading all of them. So the row carries SUMMARIES —
      a percentage and the stage each lane sits on — and a summary is small
      enough that four fit honestly.

      Detail is the zoomed state instead: one lane, full width, drawn outside
      this grid. Nothing scrolls sideways at any width.
    */
    + '@media (min-width: 640px)  { .journey-lanes { grid-template-columns: 1fr 1fr } }'
    + '@media (min-width: 1180px) { .journey-lanes { grid-template-columns: repeat(4, minmax(0, 1fr)) } }'
    /*
      THE FORK FOLLOWS THE SAME BREAKPOINTS AS THE GRID.

      The crossbar's drops are positioned in the grid's coordinate space, so
      the column count has to be a value the CSS knows — otherwise the drops
      keep four-column positions after the lanes have re-wrapped to two, and
      point at the gaps between boxes.

      `--fork-cols` is what the grid is doing; `--fork-max` is how many lanes
      this branch actually has (set inline). The lesser of the two is the
      number of boxes on the first row, which is the row the fork connects.
      Drops past that row are hidden rather than mispositioned.
    */
    + '.journey-fork { --fork-cols: 1 }'
    + '@media (min-width: 640px)  { .journey-fork { --fork-cols: 2 } }'
    + '@media (min-width: 1180px) { .journey-fork { --fork-cols: 4 } }'
    + '.journey-drop-1, .journey-drop-2, .journey-drop-3 { display: none }'
    + '@media (min-width: 640px)  { .journey-drop-1 { display: block } }'
    + '@media (min-width: 1180px) { .journey-drop-2, .journey-drop-3 { display: block } }'
  }</style>
);

/** How the segment beside a stage is painted. */
const segmentStyle = (s: ResolvedStage): React.CSSProperties => {
  if (s.state === 'done') return { background: OK };
  if (s.state === 'active' || s.state === 'blocked') {
    const age: StageAge = s.state === 'blocked' ? (s.age || 'stuck') : (s.age || 'ok');
    if (age === 'ok') {
      return {
        background: `linear-gradient(180deg, ${OK}, #6ee7b7 25%, ${OK} 50%, #6ee7b7 75%, ${OK})`,
        backgroundSize: '100% 200%',
        animation: 'journeyVein 1.8s linear infinite',
      };
    }
    return { background: AGE_COLOUR[age], animation: 'journeyBreathe 2.6s ease-in-out infinite' };
  }
  return { background: LINE };
};

/**
 * Date AND time, always, with the year.
 *
 * These stages are read months and years after the fact — during a claim, a
 * chargeback, a collection — and "12 Aug, 14:30" is useless when the question
 * is which August. The full stamp is the whole value of a timeline.
 */
const when = (ts?: number) =>
  ts ? new Date(ts).toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '';

/** How long this has been somebody's move, in the words people use for it. */
const heldFor = (since?: number) => {
  if (!since) return '';
  const ms = Math.max(0, Date.now() - since);
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  return d > 0 ? `${d}d ${h}h` : `${h}h`;
};

const StageIcon: React.FC<{ state: string }> = ({ state }) => {
  if (state === 'done') return <Check size={12} strokeWidth={3} />;
  if (state === 'blocked') return <AlertTriangle size={11} />;
  if (state === 'active') return <Clock size={11} />;
  if (state === 'skipped') return <Minus size={11} />;
  return <span className="w-1.5 h-1.5 rounded-full bg-white/70" />;
};

interface Props {
  context: JourneyContext;
  /** Rendered inside a stage when it is expanded — the panel that stage owns. */
  panels?: Record<string, React.ReactNode>;
  /** Which stage to open on load. Defaults to the one being worked on. */
  initialOpen?: string;
  onComplete?: (stageId: string, note?: string) => Promise<void>;
  onUndo?: (stageId: string) => Promise<void>;
  /**
   * Complete an EVIDENCE stage by hand, with a reason — for the order whose
   * evidence never arrives (no envelope was sent, a reader keyed elsewhere).
   * Absent means the rail offers no override, as before.
   */
  onOverride?: (stageId: string, reason: string) => Promise<void>;
  /**
   * THE PERSISTED SNAPSHOT — only the one field this file needs.
   *
   * `cx_*` stage ids land on a journey's `stageIds` all at once, the moment a
   * cancellation opens (journey-service `cancellation.ts`), so this is never
   * partial: either none of the eight are here, or all of them are. Absent
   * entirely — the default, and every existing caller's current behaviour —
   * means an ordinary order: no `cx_` ids in the filter below, so nothing is
   * appended and the rail is byte-for-byte what it always was.
   */
  journey?: { stageIds?: readonly string[] } | null;
  /**
   * STAGES TO LEAVE OUT OF THE RAIL, because their work is drawn elsewhere on
   * the page. See where this is applied below — the stage keeps every part of
   * its life except its card on this screen. Absent is every existing
   * caller's behaviour: nothing is hidden.
   */
  hideStageIds?: readonly string[];
}

/**
 * SHUT UNLESS YOU OPEN IT.
 *
 * Everything else is open by default — reading one lane should not collapse
 * the one beside it, which is the pair being compared. The exception is the
 * partner choice: once an ISO is picked that is settled history, and it sat
 * between the document pack and the partner's own work as a permanently
 * expanded block nobody was reading.
 */
const CLOSED_BY_DEFAULT = new Set(['merchant_assigned']);

/**
 * LANE PRESENTATION, in the order the lanes are drawn.
 *
 * `merchant` and `fulfilment` are the retired pair. Nothing in
 * ORDER_STAGES_V1 emits them any more, but a journey snapshot written before
 * the fork still names them, and rendering such a snapshot must not produce an
 * unlabelled box — so they keep their titles and sit last.
 */
const LANE_ORDER = [
  'card', 'payment_devices', 'pos_devices', 'accessories',
  'merchant', 'fulfilment',
] as const;

const LANE_TITLE: Record<string, string> = {
  card: 'Card processing',
  payment_devices: 'Payment devices',
  pos_devices: 'POS devices',
  accessories: 'Peripherals & accessories',
  merchant: 'Card processing',
  fulfilment: 'Hardware',
};

/**
 * Whole class strings — Tailwind's scanner cannot see an interpolated one.
 *
 * The lanes are told apart by COLOUR before they are read: four boxes with
 * identical chrome side by side is exactly the arrangement in which nobody can
 * find the one they want.
 */
const LANE_SKIN: Record<string, string> = {
  card: 'bg-rose-50/50 border-rose-200/70',
  payment_devices: 'bg-amber-50/50 border-amber-200/70',
  pos_devices: 'bg-emerald-50/50 border-emerald-200/70',
  accessories: 'bg-slate-50/70 border-slate-200',
  merchant: 'bg-white border-stone-200',
  fulfilment: 'bg-slate-50/60 border-slate-200',
};

/** The filled part of a lane's progress bar, same hue as its box. */
const LANE_BAR: Record<string, string> = {
  card: 'bg-rose-500',
  payment_devices: 'bg-amber-500',
  pos_devices: 'bg-emerald-500',
  accessories: 'bg-slate-400',
  merchant: 'bg-stone-400',
  fulfilment: 'bg-slate-400',
};

/**
 * WHERE A LANE HAS GOT TO, as one number.
 *
 * `skipped` counts as done: a lane with nothing to do on this order is
 * finished, and showing it as 60% because two of its five stages do not apply
 * would be a lane that never completes.
 */
const laneProgress = (rows: ResolvedStage[]) => {
  const total = rows.length;
  const done = rows.filter((r) => r.state === 'done' || r.state === 'skipped').length;
  return { done, total, pct: total ? Math.round((done / total) * 100) : 0 };
};

/** The stage this lane is actually sitting on, and nothing else. */
const laneNow = (rows: ResolvedStage[]): ResolvedStage | undefined =>
  rows.find((r) => r.state === 'blocked')
  || rows.find((r) => r.state === 'active')
  || rows.find((r) => r.state === 'todo');

/** The dot beside a lane title, same hue as its box. */
const LANE_DOT: Record<string, string> = {
  card: 'bg-rose-500',
  payment_devices: 'bg-amber-500',
  pos_devices: 'bg-emerald-500',
  accessories: 'bg-slate-400',
  merchant: 'bg-stone-400',
  fulfilment: 'bg-slate-400',
};

const OrderStages: React.FC<Props> = ({ context, panels, initialOpen, onComplete, onUndo, onOverride, journey, hideStageIds }) => {
  /*
    NEVER `resolveJourney(ALL_STAGES_V1, context)`. That list is every stage
    the platform knows about — passing the whole thing would draw all eight
    cancellation stages on EVERY order screen, cancelled or not. Instead,
    append only the ones this journey's own snapshot actually carries: an
    ordinary order's `stageIds` has no `cx_` ids in it, so the filter below
    empties out and this line changes nothing for it.
  */
  const stageList = [
    ...ORDER_STAGES_V1,
    ...CANCELLATION_STAGES_V1.filter((s) => journey?.stageIds?.includes(s.id)),
  ];
  /*
    STAGES WHOSE WORK IS DRAWN SOMEWHERE ELSE ON THIS PAGE.

    Hidden from the RAIL only. They stay in `ORDER_STAGES_V1`, they still
    resolve, they still auto-complete from the same evidence, they still count
    toward `journeyProgress`, and the merchant's own progress view
    (`merchantOrderProgress.ts`, which names these three in its TILL_SPINE and
    has its own customer-facing labels for them) is untouched.

    That distinction is the whole point. The handover, the tenders and the
    button layout are worked on the COUPLE card now, so a second card asking
    for the same three things is clutter — but the journey's record of them is
    not clutter, it is what tells a merchant "Setting up how you take payment"
    and what gates dispatch. Deleting the stages would take both.
  */
  const stages = resolveJourney(stageList, context)
    .filter((row) => !hideStageIds?.includes(row.def.id));

  /*
    THE SHAPE OF THE WORK, not just its order. Consecutive stages on the main
    spine are one run; the moment a laned stage appears, everything until the
    spine resumes is ONE branch section holding both lanes side by side.
  */
  type Run =
    | { kind: 'spine'; rows: ResolvedStage[] }
    | { kind: 'branch'; lanes: Record<string, ResolvedStage[]> };

  const runs: Run[] = [];
  for (const row of stages) {
    const lane = (row.def.lane || 'main') as StageLane;
    const last = runs[runs.length - 1];
    if (lane === 'main') {
      if (last?.kind === 'spine') last.rows.push(row);
      else runs.push({ kind: 'spine', rows: [row] });
    } else {
      if (last?.kind === 'branch') (last.lanes[lane] ||= []).push(row);
      else runs.push({ kind: 'branch', lanes: { [lane]: [row] } });
    }
  }

  const blockers = dispatchBlockers(stages);
  const progress = journeyProgress(stages);

  const [closed, setClosed] = useState<Set<string>>(() => new Set(
    initialOpen
      ? stages.filter((s) => s.def.id !== initialOpen).map((s) => s.def.id)
      : stages.filter((s) => CLOSED_BY_DEFAULT.has(s.def.id)).map((s) => s.def.id),
  ));
  const toggle = (id: string) => setClosed((c) => {
    const next = new Set(c);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const [busy, setBusy] = useState<string | null>(null);
  /** The stage whose "Complete manually" reason box is open, and what is typed in it. */
  const [overrideFor, setOverrideFor] = useState<string | null>(null);
  const [overrideReason, setOverrideReason] = useState('');

  const submitOverride = async (stageId: string) => {
    const reason = overrideReason.trim();
    if (!reason) return;
    setBusy(stageId);
    try {
      await onOverride?.(stageId, reason);
      setOverrideFor(null);
      setOverrideReason('');
    } finally {
      setBusy(null);
    }
  };

  const mark = async (stageId: string, undo: boolean) => {
    setBusy(stageId);
    try {
      await (undo ? onUndo?.(stageId) : onComplete?.(stageId));
    } finally {
      setBusy(null);
    }
  };

  /** The lanes present in a branch, in the drawing order, never duplicated. */
  const lanesOf = (lanes: Record<string, ResolvedStage[]>) =>
    LANE_ORDER.filter((l) => lanes[l]?.length);

  /**
   * WHICH LANE IS OPEN, or none.
   *
   * Four lanes drawn side by side at full detail is a wall: every lane's
   * stages competing for the same glance, and no way to answer "where is this
   * order" without reading all of them. So the branch has two states — an
   * overview of four boxes carrying a percentage each, and ONE lane opened to
   * full width with the others put away.
   *
   * It is also what makes the rail cheap. A closed lane renders no stages, so
   * none of their panels mount and none of their data is fetched; opening a
   * lane is what asks for that lane's data.
   */
  const [zoom, setZoom] = useState<string | null>(null);

  /** One stage. Used on the spine and inside both lanes, so they cannot drift. */
  const renderStage = (s: ResolvedStage) => {
    const isOpen = !closed.has(s.def.id);
    const panel = panels?.[s.def.id];
    const waiting = s.state === 'active' || s.state === 'blocked';
    const age: StageAge | undefined = waiting ? (s.age || 'ok') : undefined;

    /*
      WHAT THIS ROW ACTUALLY SAYS.

      `var_sheet` has two different actors depending on whether the merchant
      brought their own processor, and the resolver decides which — see
      `ownerOverride` on ResolvedStage. Resolved once here so the label, the
      chip and the "outside our facility" treatment cannot disagree.
    */
    const owner = s.ownerOverride ?? s.def.owner;
    const label = s.labelOverride ?? s.def.label;
    const hint = s.hintOverride ?? s.def.hint;
    /** An evidence stage a person may complete by hand, or has. */
    const canOverride = !!onOverride && s.def.completion !== 'manual';
    const overridden = s.evidence === 'manual override';

    /*
      WORK THAT HAPPENS SOMEWHERE ELSE LOOKS DIFFERENT.

      The partner's stage is not ours to tick and not ours to hurry, and a tile
      identical to the ones our own people act on invited exactly that reading.
      Steel and a dashed edge rather than a colour of its own: green, amber and
      red are now the language for how long something has waited, and a
      coloured partner tile would compete with it.
    */
    const external = owner === 'iso';

    const shell = external
      ? `border border-dashed ${s.state === 'blocked' || age === 'stuck' ? 'border-red-300 bg-white' : 'border-slate-300 bg-white'}`
      : `border ${s.state === 'blocked' || age === 'stuck' ? 'border-red-200 shadow-[0_2px_14px_rgba(239,68,68,.08)]'
        : waiting ? 'border-rose-200 shadow-[0_2px_14px_rgba(220,20,60,.07)]'
        : s.state === 'todo' || s.state === 'skipped' ? 'border-stone-200 border-dashed bg-stone-50/40'
        : 'border-stone-200'} bg-white`;

    return (
      <div key={s.def.id} className={`relative min-w-0 rounded-2xl mb-4 last:mb-4 ${shell}`}>
        {/* the segment of path beside this stage — its own height, through its
            own bottom margin, so the line never breaks between two tiles */}
        <span
          aria-hidden
          className="absolute -left-6 top-0 -bottom-4 w-1 rounded-sm"
          style={segmentStyle(s)}
        />
        <span
          className={`absolute -left-[33px] w-[22px] h-[22px] rounded-full grid place-items-center border-[3px] border-white z-[2] ${
            external ? 'top-[42px]' : 'top-4'} ${DOT[s.state]}`}
          style={waiting && age && age !== 'ok' ? { background: AGE_COLOUR[age], boxShadow: `0 0 0 4px ${AGE_COLOUR[age]}28` } : undefined}
        >
          <StageIcon state={s.state} />
        </span>

        {external && (
          <div className="flex items-center gap-1.5 px-4 pt-3">
            <Building2 size={10} className="text-slate-400" />
            <span className="text-[9.5px] uppercase tracking-[0.14em] font-bold text-slate-400">
              Outside our facility
            </span>
          </div>
        )}

        <div className={`px-4 min-w-0 overflow-x-auto ${external ? 'pt-2 pb-3.5' : 'py-3.5'}`}>
          <button
            onClick={() => toggle(s.def.id)}
            className="w-full text-left group"
            disabled={!panel && !hint && !canOverride}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[13px] font-semibold ${
                s.state === 'skipped' ? 'text-stone-400'
                : s.state === 'todo' ? 'text-stone-500' : 'text-stone-900'}`}
              >
                {label}
              </span>
              {s.state !== 'skipped' && s.state !== 'done' && (
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${OWNER_CHIP[owner]}`}>
                  {OWNER_LABEL[owner]}
                </span>
              )}
              {/* HOW LONG IT HAS BEEN HERE — the traffic reading. Nobody sets
                  it, and nobody can quietly leave a four-day stage looking
                  fine. */}
              {age && s.activeSince && (
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold tabular-nums ${AGE_CHIP[age]}`}>
                  ● {heldFor(s.activeSince)} here
                </span>
              )}
              {s.blocksDispatch && s.state !== 'done' && s.state !== 'skipped' && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700">
                  blocks shipping
                </span>
              )}
              {(panel || hint || canOverride) && (
                <ChevronDown
                  size={13}
                  className={`ml-auto text-stone-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
              )}
            </div>
            <p className={`text-[11.5px] mt-0.5 ${s.state === 'blocked' ? 'text-red-600' : 'text-stone-500'}`}>
              {s.detail}
            </p>
            {s.at && (
              <p className="text-[11px] text-stone-400 mt-0.5 inline-flex items-center gap-1 flex-wrap">
                <Clock size={9} /> {when(s.at)}
                {s.evidence && <span> · from {s.evidence}</span>}
              </p>
            )}
            {s.assertedBy && (
              <p className="text-[11px] text-stone-400 mt-0.5 inline-flex items-center gap-1">
                <User size={10} /> {s.assertedBy.name}
                {s.assertedBy.roleId ? ` · ${s.assertedBy.roleId}` : ''}
              </p>
            )}
          </button>

          {isOpen && (
            <div className="mt-3">
              {hint && (
                <p className="text-[11.5px] text-stone-500 leading-relaxed mb-2.5">{hint}</p>
              )}
              {panel}

              {/* The generic control for a stage a person completes and that
                  has no panel of its own. Whoever presses it is recorded by
                  name and role. */}
              {!panel && s.def.completion === 'manual' && s.state !== 'skipped' && onComplete && (
                s.state === 'done'
                  ? (
                    <button
                      onClick={() => mark(s.def.id, true)}
                      disabled={busy === s.def.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-200 text-[11px] font-semibold text-stone-600 hover:bg-stone-50 disabled:opacity-50"
                    >
                      {busy === s.def.id ? <RubyGemMark size={11} drawing color="currentColor" /> : <RotateCcw size={11} />}
                      Undo
                    </button>
                  ) : (
                    <button
                      onClick={() => mark(s.def.id, false)}
                      disabled={busy === s.def.id}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-[11px] font-semibold disabled:opacity-50"
                    >
                      {busy === s.def.id ? <RubyGemMark size={11} drawing color="currentColor" /> : <Check size={11} strokeWidth={3} />}
                      Mark {s.def.label.toLowerCase()}
                    </button>
                  )
              )}
              {/*
                COMPLETE MANUALLY — the way out when a stage's evidence will
                never arrive on this order. Needs a reason; the stage then reads
                "Completed manually by …" and Undo hands it back to the evidence.
              */}
              {canOverride && overridden && (
                <button
                  onClick={() => mark(s.def.id, true)}
                  disabled={busy === s.def.id}
                  className="mt-3 inline-flex min-h-[44px] items-center gap-1.5 px-3 rounded-lg border border-stone-200 text-[11px] font-semibold text-stone-600 hover:bg-stone-50 disabled:opacity-50"
                >
                  {busy === s.def.id ? <RubyGemMark size={11} drawing color="currentColor" /> : <RotateCcw size={11} />}
                  Undo manual completion
                </button>
              )}
              {canOverride && !overridden && s.state !== 'done' && (
                overrideFor === s.def.id ? (
                  <div className="mt-3 rounded-xl border border-stone-200 bg-stone-50/60 p-3">
                    <label className="block text-[11px] font-semibold text-stone-700 mb-1.5">
                      Why is this being completed by hand?
                    </label>
                    <input
                      autoFocus
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') submitOverride(s.def.id); if (e.key === 'Escape') setOverrideFor(null); }}
                      maxLength={300}
                      placeholder={s.def.id === 'agreement' ? 'e.g. No agreement on file for this order' : 'e.g. Verified directly in the processor portal'}
                      className="w-full min-h-[44px] rounded-lg border border-stone-300 bg-white px-3 text-[12.5px] text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-rose-400"
                    />
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        onClick={() => submitOverride(s.def.id)}
                        disabled={!overrideReason.trim() || busy === s.def.id}
                        className="inline-flex min-h-[44px] items-center gap-1.5 px-3.5 rounded-lg bg-stone-900 hover:bg-black text-white text-[11px] font-semibold disabled:opacity-40"
                      >
                        {busy === s.def.id ? <RubyGemMark size={11} drawing color="currentColor" /> : <Check size={11} strokeWidth={3} />}
                        Complete manually
                      </button>
                      <button
                        onClick={() => { setOverrideFor(null); setOverrideReason(''); }}
                        className="inline-flex min-h-[44px] items-center px-3 rounded-lg text-[11px] font-semibold text-stone-500 hover:text-stone-800"
                      >
                        Cancel
                      </button>
                      <span className="text-[10.5px] text-stone-400">Recorded with your name and the time.</span>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => { setOverrideFor(s.def.id); setOverrideReason(s.def.id === 'agreement' && s.state === 'skipped' ? 'No agreement on file for this order' : ''); }}
                    className="mt-3 inline-flex min-h-[44px] items-center gap-1.5 px-3 rounded-lg border border-stone-300 text-[11px] font-semibold text-stone-700 hover:bg-stone-50"
                  >
                    <Check size={11} strokeWidth={3} />
                    {s.def.id === 'agreement' ? 'No agreement — mark done' : 'Complete manually'}
                  </button>
                )
              )}
              {!panel && s.def.completion === 'manual' && !onComplete && s.state !== 'done' && s.state !== 'skipped' && (
                <p className="text-[11.5px] text-amber-700">
                  The journey record is unavailable, so this cannot be marked done yet.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  /**
   * WHERE ONE LINE BECOMES TWO, drawn rather than implied.
   *
   * A stem, a crossbar and a drop landing exactly on each lane's own rail. The
   * elbow-and-label this replaces left both lanes starting from nothing —
   * two lines that happened to end near each other rather than a join.
   */
  /**
   * THE CROSSBAR AND ITS DROPS, for however many lanes there are.
   *
   * This used to be two hand-placed drops and a `calc(50% + 61px)` bar — the
   * geometry of exactly two lanes. With four it reached the third box and
   * stopped, so the fourth lane hung unconnected at both ends and did not
   * read as part of the fork at all.
   *
   * Now it is derived. Each lane's drop lands on that lane's OWN internal
   * rail (`left-[23px]` inside the box), not on the box's centre, so the drop
   * continues the line the lane already draws instead of pointing at the
   * middle of nothing. The bar runs from the spine to the last of those
   * drops — which is near the last box's left edge, and so still stops clear
   * of the caption on the right.
   *
   * `GRID_GAP` must stay in step with the `gap-3` on `.journey-lanes`: the
   * drops are positioned in the same coordinate space as the columns.
   */
  const GRID_GAP = 12;
  const Fork: React.FC<{ up?: boolean; done?: boolean; label: string; lanes: number }> = ({
    up, done, label, lanes,
  }) => {
    const paint = done ? OK : LINE;
    const n = Math.max(1, lanes);
    /* How many boxes sit on the fork's row — the lesser of what the grid is
       drawing and what this branch actually has. Resolved in CSS, because
       only the CSS knows which breakpoint is live. */
    const N = 'min(var(--fork-cols), var(--fork-max))';
    const col = `((100% - (${N} - 1) * ${GRID_GAP}px) / ${N})`;
    /* Lane i's rail: i whole columns across, i gaps, then the rail's own 23px. */
    const dropAt = (i: number) => `calc(${col} * ${i} + ${GRID_GAP * i + 23}px)`;
    /* From the spine (which starts 24px left of this box) to the last drop. */
    const barWidth = `calc(${col} * (${N} - 1) + (${N} - 1) * ${GRID_GAP}px + ${23 + 24}px)`;

    return (
      <div
        className="journey-fork relative h-[38px] ml-[34px]"
        style={{ ['--fork-max' as any]: n }}
        aria-hidden
      >
        <span className="absolute left-[-24px] w-1 rounded-sm" style={{ background: paint, top: up ? 17 : 0, height: 21 }} />
        <span className="absolute left-[-24px] top-[17px] h-1 rounded-sm" style={{ background: paint, width: barWidth }} />
        {Array.from({ length: n }, (_, i) => (
          <span
            key={i}
            className={`absolute w-1 rounded-sm journey-drop-${i}`}
            style={{ background: paint, left: dropAt(i), top: up ? 0 : 17, height: 21 }}
          />
        ))}
        <span className="absolute right-0 top-[9px] text-[10px] uppercase tracking-[0.12em] font-bold text-stone-400">
          {label}
        </span>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
      <JourneyStyle />
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
        <h3 className="text-[11px] uppercase tracking-[0.12em] font-bold text-stone-500">Order stages</h3>
        <span className="text-[11.5px] text-stone-400">{progress.done} of {progress.total} complete</span>
      </div>

      <div className="h-1.5 rounded-full bg-stone-200 overflow-hidden mb-4">
        <div
          className="h-full rounded-full bg-emerald-500 transition-all"
          style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }}
        />
      </div>

      {blockers.length > 0 && (
        <div className="border-l-[3px] border-red-500 bg-red-50/70 rounded-r-xl px-4 py-3 mb-4">
          <p className="text-[12.5px] text-stone-700 leading-relaxed">
            <b className="text-stone-900">This order cannot ship yet.</b>{' '}
            {blockers.map((b) => b.def.label).join(' · ')} — {blockers.length === 1 ? 'that stage is' : 'those stages are'} holding dispatch.
          </p>
        </div>
      )}

      {runs.map((run, ri) => (
        run.kind === 'spine'
          ? <div key={`spine-${ri}`} className="pl-[34px]">{run.rows.map((s) => renderStage(s))}</div>
          : (
            <div key={`branch-${ri}`}>
              <Fork
                label="These run at the same time"
                done={lanesOf(run.lanes).some((l) => run.lanes[l][0].state !== 'todo')}
                /* Zoomed in there is one box, so the fork connects one. */
                lanes={zoom && run.lanes[zoom] ? 1 : lanesOf(run.lanes).length}
              />

              {/*
                THE OVERVIEW. Four boxes, a percentage each, and the one stage
                each lane is actually sitting on — which is the whole question
                "where is this order" and used to need reading four columns of
                stages to answer.
              */}
              {(!zoom || !run.lanes[zoom]) ? (
                <div className="journey-lanes grid grid-cols-1 gap-3 items-stretch ml-[34px]">
                  {lanesOf(run.lanes).map((lane) => {
                    const rows = run.lanes[lane];
                    const { done, total, pct } = laneProgress(rows);
                    const now = laneNow(rows);
                    const stuck = rows.some((r) => r.state === 'blocked');
                    return (
                      <button
                        key={lane}
                        type="button"
                        onClick={() => setZoom(lane)}
                        aria-label={`Open ${LANE_TITLE[lane]} — ${pct}% complete`}
                        className={`group relative min-w-0 text-left rounded-[18px] p-3.5 border transition-shadow hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-stone-400 ${
                          LANE_SKIN[lane] || 'bg-white border-stone-200'
                        }`}
                      >
                        <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.08em] font-bold text-stone-500">
                          <span aria-hidden className={`w-2 h-2 rounded-sm shrink-0 ${LANE_DOT[lane] || 'bg-stone-400'}`} />
                          <span className="truncate">{LANE_TITLE[lane]}</span>
                        </span>

                        <span className="mt-2 flex items-baseline gap-1.5">
                          <span className="text-[22px] leading-none font-bold text-stone-800 tabular-nums">{pct}%</span>
                          <span className="text-[11px] text-stone-500 tabular-nums">{done}/{total} steps</span>
                        </span>

                        <span aria-hidden className="mt-2 block h-1.5 w-full rounded-full bg-white/70 overflow-hidden">
                          <span
                            className={`block h-full rounded-full ${stuck ? 'bg-red-500' : (LANE_BAR[lane] || 'bg-stone-400')}`}
                            style={{ width: `${pct}%` }}
                          />
                        </span>

                        {/* What it is waiting on. The reason to open this lane. */}
                        <span className="mt-2.5 block min-w-0">
                          {now ? (
                            <>
                              <span className="block truncate text-[12px] font-medium text-stone-700">{now.labelOverride ?? now.def.label}</span>
                              <span className="mt-1 inline-flex items-center gap-1.5">
                                <span className={`text-[9.5px] font-semibold rounded px-1.5 py-0.5 ${OWNER_CHIP[now.ownerOverride ?? now.def.owner]}`}>
                                  {OWNER_LABEL[now.ownerOverride ?? now.def.owner]}
                                </span>
                                {now.state === 'blocked' && (
                                  <span className="text-[9.5px] font-semibold rounded px-1.5 py-0.5 bg-red-100 text-red-700">Blocked</span>
                                )}
                              </span>
                            </>
                          ) : (
                            <span className="flex items-center gap-1.5 text-[12px] font-medium text-emerald-700">
                              <Check size={12} strokeWidth={3} /> Lane complete
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                /*
                  ZOOMED IN. One lane, full width, the other three put away —
                  and with them their panels, which is why nothing else is
                  fetched while you work this one.
                */
                <div className="ml-[34px]">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() => setZoom(null)}
                      className="inline-flex items-center gap-1.5 min-h-[36px] px-3 rounded-lg border border-stone-300 bg-white text-[12px] font-semibold text-stone-600 hover:bg-stone-50 hover:text-stone-900"
                    >
                      <ChevronLeft size={14} /> All {lanesOf(run.lanes).length} lanes
                    </button>
                    {lanesOf(run.lanes).filter((l) => l !== zoom).map((l) => {
                      const p = laneProgress(run.lanes[l]);
                      return (
                        <button
                          key={l}
                          type="button"
                          onClick={() => setZoom(l)}
                          className="inline-flex items-center gap-1.5 min-h-[36px] px-2.5 rounded-lg border border-stone-200 bg-white text-[11.5px] font-medium text-stone-500 hover:text-stone-900 hover:border-stone-300"
                        >
                          <span aria-hidden className={`w-2 h-2 rounded-sm ${LANE_DOT[l] || 'bg-stone-400'}`} />
                          <span className="truncate max-w-[12rem]">{LANE_TITLE[l]}</span>
                          <span className="tabular-nums text-stone-400">{p.pct}%</span>
                        </button>
                      );
                    })}
                  </div>

                  <div className={`relative min-w-0 rounded-[18px] p-3 border ${LANE_SKIN[zoom] || 'bg-white border-stone-200'}`}>
                    {/* a backing line the full height of the box, so the lane
                        always reaches the rejoin whatever it contains */}
                    <span aria-hidden className="absolute left-[23px] top-0 bottom-0 w-1 rounded-sm" style={{ background: LINE }} />
                    {/* …and the piece from the top edge to the first tile,
                        green once the lane has started */}
                    <span
                      aria-hidden
                      className="absolute left-[23px] top-0 h-[42px] w-1 rounded-sm"
                      style={{ background: run.lanes[zoom][0].state === 'todo' ? LINE : OK }}
                    />
                    <p className="h-4 leading-4 pl-[30px] mb-3 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.08em] font-bold text-stone-500">
                      <span aria-hidden className={`w-2 h-2 rounded-sm shrink-0 ${LANE_DOT[zoom] || 'bg-stone-400'}`} />
                      <span className="truncate">{LANE_TITLE[zoom]}</span>
                      <span className="ml-auto tabular-nums text-stone-400">{laneProgress(run.lanes[zoom]).pct}%</span>
                    </p>
                    <div className="pl-[30px] min-w-0">
                      {run.lanes[zoom].map((s) => renderStage(s))}
                    </div>
                  </div>
                </div>
              )}

              <Fork
                up
                label={`All ${lanesOf(run.lanes).length} done before it leaves`}
                lanes={zoom && run.lanes[zoom] ? 1 : lanesOf(run.lanes).length}
              />
            </div>
          )
      ))}
    </div>
  );
};

export default OrderStages;
